/**
 * Buy pill bar (snippets/soulog-buy-pill.liquid), adapted from the original subscribe pill bar (Figma "Website" node 589:883).
 *
 * When it appears (2026-09-27 requirement verbatim: stay fixed and always visible, except inside the shop module;
 * same day follow-up: "the first module should not show it"):
 * - hidden while the page's first section (home page = launch hero) still fills more than the top 1/3 of the viewport;
 *   2026-09-28: it used to wait until the hero had fully left, then popped in right over the second section's heading,
 *   so it now comes in while 1/3 of the first screen is still showing
 * - after that visible, including while scrolling and over the footer
 * - hidden only while the shop module content (.ssm__inner) is in view -- its top has crossed half the viewport and its bottom
 *   hasn't left the top edge; the outer wrapper includes top/bottom spacing, so judging by it would hide the pill before the product is even visible
 *
 * The buy button calls the shop module's own buyNow() (2026-10-12), so the current color / quantity / selected gift
 * and attribution are all shared; since 2026-10-20 that adds to the cart and goes to the cart page (was: straight to checkout).
 * When there is no usable shop module (not on the page, sold out) the click is left to the site-wide
 * "scroll back to the shop module" delegate in assets/soulog-scroll-to-shop.js; when the add to cart fails we
 * scroll there ourselves so the error under the buy button is visible.
 */
if (!customElements.get("soulog-buy-pill")) {
	class SoulogBuyPill extends HTMLElement {
		connectedCallback() {
			const watched = document.getElementById(this.dataset.watch);
			this.content = watched ? watched.querySelector(".ssm__inner") || watched : null;
			this.inShop = false;
			// The first section of the page body; skipped when it is the shop module itself (product page, KV disabled) --
			// the in-shop rule below already covers that case
			const first = document.querySelector("#main-content > .shopify-section");
			this.first = first && !(watched && first.contains(watched)) ? first : null;
			this.inFirst = false;
			// One measurement per frame at most; getBoundingClientRect on every scroll event would force extra layouts
			this.onScroll = () => {
				if (this.frame) return;
				this.frame = requestAnimationFrame(() => {
					this.frame = 0;
					this.sync();
				});
			};
			window.addEventListener("scroll", this.onScroll, { passive: true });
			window.addEventListener("resize", this.onScroll, { passive: true });
			this.sync();
			this.setupOffer();
			this.setupBuy(watched);
			this.setupPrice(watched);
		}

		/**
		 * Price row follows the shop module's Total (2026-10-21: "the price shouldn't be fixed, it should follow what I picked").
		 * The buy button buys that exact order (color / quantity / paid add-on), so both show one number from one algorithm:
		 * the shop module fires "ssm:total" with its orderTotals() on every renderTotal(). Its first render may run
		 * before this pill is connected, so we also pull once if the shop is already upgraded.
		 */
		setupPrice(watched) {
			const shop = watched && watched.querySelector("soulog-shop-module");
			this.priceNow = this.querySelector("[data-sbp-price-now]");
			if (!shop || !this.priceNow) return;
			this.priceWas = this.querySelector("[data-sbp-price-was]");
			this.priceArrow = this.querySelector("[data-sbp-arrow]");
			this.priceBadge = this.querySelector("[data-sbp-badge]");
			this.giftSave = this.querySelector("[data-sbp-save]");
			this.thumb = this.querySelector(".sbp__thumb");
			this.buyLink = this.querySelector("[data-sbp-buy]");
			if (this.buyLink) this.buyLabel = this.buyLink.textContent.trim();
			this.shop = shop;
			this.thumb = this.querySelector(".sbp__thumb");
			this.onTotal = (event) => {
				this.renderPrice(event.detail);
				this.renderThumb();
			};
			shop.addEventListener("ssm:total", this.onTotal);
			if (typeof shop.orderTotals === "function") this.renderPrice(shop.orderTotals());
		}

		/**
		 * Thumbnail follows the picked color too (2026-10-22: "the product I picked above, the floating bar didn't change").
		 * Same source as the Liquid first render (the variant's featured_image), so before/after picking agree.
		 * Every color change goes through renderTotal(), so ssm:total is the one hook; no image = keep the current one.
		 */
		renderThumb() {
			if (!this.thumb || typeof this.shop.currentVariant !== "function") return;
			const variant = this.shop.currentVariant();
			const src = variant && variant.featured_image && variant.featured_image.src;
			if (!src) return;
			const sized = (w) => `${src}${src.includes("?") ? "&" : "?"}width=${w}`;
			this.thumb.srcset = `${sized(64)} 64w, ${sized(128)} 128w`;
			this.thumb.src = sized(128);
		}

		renderPrice(totals) {
			if (!totals || typeof this.shop.formatMoney !== "function") return;
			const { cents, discounted, finalCents } = totals;
			this.priceNow.textContent = this.shop.formatMoney(finalCents);
			if (this.priceWas) {
				this.priceWas.textContent = this.shop.formatMoney(cents);
				this.priceWas.hidden = !discounted;
			}
			if (this.priceArrow) this.priceArrow.hidden = !discounted;
			// "SAVE 10%" only holds while the discount applies (it has a minimum amount)
			if (this.priceBadge) this.priceBadge.hidden = !discounted;
			if (this.giftSave) this.giftSave.hidden = !discounted;
			this.renderThumb();
			this.renderSoldOut();
		}

		/**
		 * Sold-out color (2026-10-22: picking one still said Buy Now): label says Sold out and the button is
		 * disabled like the module's own — a "Sold out" you can still press reads as broken (user, same day).
		 * It's an <a>, so "disabled" = aria-disabled + out of the tab order + no pointer events (CSS), and the
		 * click handler swallows anything that still gets through.
		 */
		renderSoldOut() {
			if (!this.buyLink || typeof this.shop.isAvailable !== "function") return;
			const ok = this.shop.isAvailable();
			this.buyLink.textContent = ok ? this.buyLabel : this.shop.soldOutLabel || "Sold out";
			if (ok) {
				this.buyLink.removeAttribute("aria-disabled");
				this.buyLink.removeAttribute("tabindex");
			} else {
				this.buyLink.setAttribute("aria-disabled", "true");
				this.buyLink.setAttribute("tabindex", "-1");
			}
			this.classList.toggle("is-sold-out", !ok);
		}

		/**
		 * Thumbnail follows the picked color too (2026-10-22: purple picked, pill still showed the grey one).
		 * Uses the variant's featured image — the cut-out front view (soulog-front-<color>), same source the
		 * Liquid first paint uses — and only falls back to the swatch's host image when a variant has none.
		 * (The host image is the big gallery shot; at 52px it reads as a grey blob.)
		 * "ssm:total" fires on every color change, so this rides on the price update.
		 */
		renderThumb() {
			if (!this.thumb) return;
			const variant = typeof this.shop.currentVariant === "function" ? this.shop.currentVariant() : null;
			const swatch = this.shop.querySelector("[data-ssm-swatch].is-active");
			const src =
				(variant && variant.featured_image && variant.featured_image.src) || (swatch && swatch.dataset.hostImg);
			if (!src || src === this.thumbSrc) return;
			this.thumbSrc = src;
			const sized = (w) => {
				try {
					const url = new URL(src, location.href);
					url.searchParams.set("width", w);
					return url.href;
				} catch (e) {
					return src;
				}
			};
			this.thumb.srcset = `${sized(64)} 64w, ${sized(128)} 128w`;
			this.thumb.src = sized(128);
		}

		setupBuy(watched) {
			const button = this.querySelector("[data-sbp-buy]");
			const shop = watched && watched.querySelector("soulog-shop-module");
			if (!button || !shop) return;
			button.addEventListener("click", (event) => {
				// Modifier-key clicks (open in new tab, etc.) are left to the browser
				if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return;
				if (button.getAttribute("aria-busy") === "true") {
					event.preventDefault();
					return;
				}
				// Sold out: do nothing — preventDefault also makes the scroll-to-shop delegate stand down
				// (it checks defaultPrevented). Normally unreachable: CSS gives it pointer-events: none.
				if (button.getAttribute("aria-disabled") === "true") {
					event.preventDefault();
					return;
				}
				const buy = shop.querySelector("[data-ssm-buy]");
				if (typeof shop.buyNow !== "function" || !buy || buy.disabled) return;
				// Let the click keep bubbling: the cta_click tracking delegate on document still has to see it
				event.preventDefault();
				shop.buyNow(button).then((ok) => {
					if (ok) return;
					const target = shop.querySelector(".ssm__inner") || shop;
					if (typeof window.__soulogScrollToShopEl === "function") window.__soulogScrollToShopEl(target);
					else target.scrollIntoView({ behavior: "smooth", block: "start" });
				});
			});
			// Back from the cart page via bfcache: the button would otherwise stay busy forever
			this.onPageShow = (event) => {
				if (event.persisted) button.removeAttribute("aria-busy");
			};
			window.addEventListener("pageshow", this.onPageShow);
		}

		/**
		 * New-customer offer tag next to the price: clicking it opens the subscribe popup (sections/overlay-popup.liquid,
		 * the only modal-dialog with data-popup). The code is only sent after signing up, so the tag is an entry point, not a price.
		 * Stays hidden when there is no popup on the page (section disabled / logged-in customer / other-page setting) —
		 * a tag that does nothing when clicked is worse than no tag — and when the visitor already subscribed
		 * (modal-dialog.js writes { subscribed: true } under localStorage[popup.id]).
		 */
		setupOffer() {
			// Two entry points share the same rule: the offer chip (only when there is no auto discount) and the
			// subscribe link (2026-10-12, under the trust line). Both open the popup; show() ignores the 14-day cooldown.
			const triggers = this.querySelectorAll("[data-sbp-offer], [data-sbp-subscribe]");
			if (!triggers.length) return;
			const popup = document.querySelector("modal-dialog[data-popup]");
			if (!popup || typeof popup.show !== "function") return;
			if (typeof popup.readSuppression === "function" && popup.readSuppression().subscribed) return;
			triggers.forEach((trigger) => {
				trigger.hidden = false;
				trigger.addEventListener("click", () => popup.show(trigger));
			});
		}

		disconnectedCallback() {
			document.body.classList.remove("has-buy-pill");
			window.removeEventListener("scroll", this.onScroll);
			window.removeEventListener("resize", this.onScroll);
			if (this.onPageShow) window.removeEventListener("pageshow", this.onPageShow);
			if (this.onTotal) this.shop.removeEventListener("ssm:total", this.onTotal);
			if (this.frame) cancelAnimationFrame(this.frame);
		}

		sync() {
			if (this.content) {
				const rect = this.content.getBoundingClientRect();
				this.inShop = rect.top < window.innerHeight * 0.5 && rect.bottom > 0;
			}
			if (this.first) this.inFirst = this.first.getBoundingClientRect().bottom > window.innerHeight / 3;
			const visible = !this.inShop && !this.inFirst;
			this.classList.toggle("is-visible", visible);
			document.body.classList.toggle("has-buy-pill", visible);
			// GA4 buy_bar_view: the first time the bar actually shows, once per page view. The bar is fixed and
			// hidden most of the time, so the theme's IntersectionObserver can't judge it (it's marked manual);
			// layout/theme.liquid owns params / dedupe / the push, this only decides "when".
			if (visible && !this.viewReported && typeof window.__soulogGa4View === "function") {
				this.viewReported = true;
				window.__soulogGa4View(this);
			}
		}
	}

	customElements.define("soulog-buy-pill", SoulogBuyPill);
}
